/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

// @ts-ignore
import Address from '@msdyn365-commerce-modules/order-management/dist/lib/common/address';
// @ts-ignore
import LabelComponent from '@msdyn365-commerce-modules/order-management/dist/lib/common/label';
// @ts-ignore
import { defaultImageSettings, getDateTimeslot, getSalesStatusCode, getSalesStatusCode, IDeliveryType, IDeliveryType, IModuleComponentProps, IModuleComponentProps, ISalesStatusCode } from '@msdyn365-commerce-modules/order-management/dist/lib/common/utilities';
import { getFallbackImageUrl, getProductUrlSync } from '@msdyn365-commerce-modules/retail-actions';
import { Button, getPayloadObject, getTelemetryAttributes, INodeProps, ITelemetryContent, TelemetryConstant } from '@msdyn365-commerce-modules/utilities';
import { CartLineItemComponent } from '@msdyn365-commerce/components';
import { Image } from '@msdyn365-commerce/core';
import { SalesLine, Shipment, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import * as React from 'react';

export type ISalesStatusStringMap = {
    [x in ISalesStatusCode]: string | undefined;
};

export type IDeliveryTypeStringMap = {
    [x in IDeliveryType]: string | undefined;
};

export interface ISalesLineResources {
    productDimensionTypeSize: string;
    productDimensionTypeColor: string;
    productDimensionTypeStyle: string;
    salesLineQuantityText: string;
    configString: string;
    shipToLabel: string;
    storeLabel: string;
    phoneLabel: string;
    phoneAriaLabel: string;
    buyItAgainAriaLabel: string;
    buyItAgainLabel: string;
    orderStatusReadyForPickup: string;
    orderStatusPickedUp: string;
    orderStatusShipped: string;
    orderStatusCanceled: string;
    orderStatusProcessing: string;
    pickedUpSalesLines: string;
    deliveredSalesLines: string;
    carryOutSalesLines: string;
    trackingLabel: string;
    trackingAriaLabel: string;
    trackingComingSoonLabel: string;
    discountStringText: string;
    orderStatusEmailed: string;
    emailSalesLines: string;
    pickupDateTimeslotText: string;
    pickupTimeslotPlaceHolder: string;
    shippingCharges?: string;
}

export interface IGetSalesLineInput {
    moduleName: string;
    moduleComponentProps: IModuleComponentProps;
    product: SimpleProduct;
    shipment?: Shipment;
    salesLine: SalesLine;
    isCurrentChannel: boolean;
    resources: ISalesLineResources;
    imageOnly?: boolean;
    showCompleteAddress?: boolean;
    showTimeslot: boolean;
    telemetryContent?: ITelemetryContent;
}

export interface ISalesLine {
    salesLineProps: INodeProps;
    salesLine: React.ReactNode;
    address?: React.ReactNode;
    buyAgainButton?: React.ReactNode;
    salesStatus: React.ReactNode;
    deliveryType: React.ReactNode;
    trackingInfo: React.ReactNode;
    pickupDateTimeslot: React.ReactNode;

    // row data
    data: {
        salesLine: SalesLine;
        product: SimpleProduct;
        shipment?: Shipment;
        deliveryType: IDeliveryType;
        productUrl: string;
        salesStatus: ISalesStatusCode;
    };
}

// tslint:disable-next-line:max-func-body-length cyclomatic-complexity
export const getSalesLine = ({
    moduleName,
    moduleComponentProps: {
        id,
        typeName,
        context,
        telemetry,
        config: { imageSettings, showShippingChargesForLineItems }
    },
    showCompleteAddress,
    product,
    shipment,
    salesLine,
    isCurrentChannel,
    imageOnly,
    showTimeslot,
    resources: {
        productDimensionTypeSize: sizeString,
        productDimensionTypeColor: colorString,
        productDimensionTypeStyle: styleString,
        salesLineQuantityText: quantityDisplayString,
        configString,
        shipToLabel,
        storeLabel,
        phoneLabel,
        phoneAriaLabel,
        buyItAgainLabel,
        buyItAgainAriaLabel,
        orderStatusReadyForPickup,
        orderStatusProcessing,
        orderStatusShipped,
        orderStatusPickedUp,
        orderStatusCanceled,
        orderStatusEmailed,
        pickedUpSalesLines,
        deliveredSalesLines,
        carryOutSalesLines,
        emailSalesLines,
        trackingLabel,
        trackingAriaLabel,
        trackingComingSoonLabel,
        discountStringText,
        pickupDateTimeslotText,
        pickupTimeslotPlaceHolder,
        shippingCharges
    },
    telemetryContent
}: IGetSalesLineInput): ISalesLine => {
    const salesStatusStringMap: ISalesStatusStringMap = {
        canceled: orderStatusCanceled,
        readyForPickup: orderStatusReadyForPickup,
        pickedUp: orderStatusPickedUp,
        processing: orderStatusProcessing,
        shipped: orderStatusShipped,
        unknown: undefined,
        emailed: orderStatusEmailed
    };

    const deliveryTypeStringMap: IDeliveryTypeStringMap = {
        pickUp: pickedUpSalesLines,
        ship: deliveredSalesLines,
        carryOut: carryOutSalesLines,
        email: emailSalesLines
    };

    const gridSettings = context.request.gridSettings;
    const pickupDeliveryModeCode = context.request.channel && context.request.channel.PickupDeliveryModeCode;
    const emailDeliveryModeCode = context.request.channel && context.request.channel.EmailDeliveryModeCode;
    const productUrl = getProductUrlSync(product, context.actionContext, undefined);
    const deliveryType =
        !!pickupDeliveryModeCode && pickupDeliveryModeCode !== '' && pickupDeliveryModeCode === salesLine.DeliveryMode
            ? 'pickUp'
            : !!emailDeliveryModeCode && emailDeliveryModeCode !== '' && emailDeliveryModeCode === salesLine.DeliveryMode
                ? 'email'
            : !!salesLine.DeliveryMode
            ? 'ship'
            : 'carryOut';
    const salesStatus = getSalesStatusCode(deliveryType, salesLine.SalesStatusValue);
    const salesStatusString = salesStatusStringMap[salesStatus];
    const salesStatusComp = salesStatusString && <LabelComponent className={`${moduleName}__sales-status`} label={salesStatusString} />;
    const deliveryTypeString = deliveryTypeStringMap[deliveryType];
    const deliveryTypeComp = deliveryTypeString && <LabelComponent className={`${moduleName}__devliery-type`} label={deliveryTypeString} />;
    const trackingInfo = isCurrentChannel && deliveryType === 'ship' && (
        <span className={`${moduleName}__tracking-info`}>
            {trackingLabel}
            {shipment ? (
                <a className={`${moduleName}__tracking-link`} href={shipment.TrackingUrl} aria-label={trackingAriaLabel}>
                    {shipment.TrackingNumber}
                </a>
            ) : (
                <>{trackingComingSoonLabel}</>
            )}
        </span>
    );
    const payLoad = getPayloadObject('click', telemetryContent!, TelemetryConstant.BuyItAgain);
    const buyItAgainAttributes = getTelemetryAttributes(telemetryContent!, payLoad);
    const fallbackImageUrl = getFallbackImageUrl(product.ItemId, context.actionContext.requestContext.apiSettings);

    const canShowTimeslot: boolean = showTimeslot
        && deliveryType === 'pickUp'
        && salesLine.PickupTimeslotStartDateTime !== undefined
        && salesLine.PickupTimeslotEndDateTime !== undefined;

    const pickupDateTimeslot = canShowTimeslot ? getDateTimeslot(pickupTimeslotPlaceHolder, salesLine.PickupTimeslotStartDateTime, salesLine.PickupTimeslotEndDateTime, context.request.locale) : '';
    const pickupDateTimeslotInfo = canShowTimeslot && (
        <div className={`${moduleName}__pickup-date-timeslot`}>
            <div className={`${moduleName}__pickup-date-timeslot-title`}>{pickupDateTimeslotText}</div>
            <div className={`${moduleName}__pickup-date-timeslot-content`}>{pickupDateTimeslot}</div>
        </div>
    );
    return {
        data: {
            salesLine,
            productUrl,
            deliveryType,
            salesStatus,
            product,
            shipment
        },
        salesLineProps: { className: `${moduleName}__sales-line` },
        salesLine: imageOnly ? (
            <div className={`${moduleName}__sales-line-picture`}>
                <Image
                    className={`${moduleName}__sales-line-image`}
                    altText={product.Name}
                    src={product.PrimaryImageUrl || ''}
                    fallBackSrc={fallbackImageUrl}
                    gridSettings={gridSettings!}
                    imageSettings={imageSettings || defaultImageSettings}
                    loadFailureBehavior='empty'
                />
            </div>
        ) : (
            <CartLineItemComponent
                id={id}
                typeName={typeName}
                context={context}
                resources={{
                    sizeString,
                    colorString,
                    styleString,
                    quantityDisplayString,
                    configString,
                    discountStringText,
                    inputQuantityAriaLabel: '',
                    originalPriceText: '',
                    currentPriceText: '',
                    shippingChargesText: shippingCharges || ''
                }}
                primaryImageUrl={product.PrimaryImageUrl}
                productUrl={productUrl}
                imageSettings={imageSettings || defaultImageSettings}
                gridSettings={gridSettings!}
                isSalesLine={true}
                data={{cartLine: salesLine, product: product}}
                showShippingChargesForLineItems={showShippingChargesForLineItems}
            />
        ),
        address: salesLine.ShippingAddress && (
            <Address
                moduleName={moduleName}
                address={salesLine.ShippingAddress}
                resource={{ shipToLabel, storeLabel, phoneLabel, phoneAriaLabel }}
                deliveryType={deliveryType}
                showCompleteAddress={showCompleteAddress}
            />
        ),
        deliveryType: deliveryTypeComp,
        salesStatus: salesStatusComp,
        trackingInfo,
        buyAgainButton: isCurrentChannel && (
            <Button
                href={productUrl}
                // aria-label={name && buyItAgainAriaLabel ? (buyItAgainAriaLabel || '').replace('{productName}', name) : ''}
                className={`${moduleName}__sales-line-btn-buy-it-again`}
                {...buyItAgainAttributes}
            >
                {buyItAgainLabel}
            </Button>
        ),
        pickupDateTimeslot: pickupDateTimeslotInfo,
    };
};
